// Weekend rhythm — client mirror of server/utils/weekendRhythm.js (the client
// is built separately and cannot import server code). Keep weekIndex,
// workingSide and freeWeekends identical; server/utils/testWeekendRhythm.js
// compares both.

export const REFERENCE_MONDAY = '2024-01-01';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function parseDate(str) { return new Date(str + 'T00:00:00Z'); }
function addDays(dateStr, n) {
  const d = parseDate(dateStr);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
function mondayOf(dateStr) {
  const day = parseDate(dateStr).getUTCDay();
  return addDays(dateStr, day === 0 ? -6 : 1 - day);
}
function isWeekendDay(dateStr) {
  const d = parseDate(dateStr).getUTCDay();
  return d === 0 || d === 6;
}

// Whole weeks between the Monday of dateStr and the reference Monday
export function weekIndex(dateStr) {
  return Math.round((parseDate(mondayOf(dateStr)) - parseDate(REFERENCE_MONDAY)) / (7 * 86400000));
}

// Rhythm that works the weekend of dateStr: 'A' (even index) or 'B' (odd)
export function workingSide(dateStr) {
  return Math.abs(weekIndex(dateStr)) % 2 === 0 ? 'A' : 'B';
}

// Weekends touching [start, end]: [{ saturday, side, days: [in-range dates] }]
export function weekendsInRange(start, end) {
  const out = [];
  const bySat = {};
  for (let d = start; d <= end; d = addDays(d, 1)) {
    if (!isWeekendDay(d)) continue;
    const sat = parseDate(d).getUTCDay() === 0 ? addDays(d, -1) : d;
    if (!bySat[sat]) {
      bySat[sat] = { saturday: sat, side: workingSide(sat), days: [] };
      out.push(bySat[sat]);
    }
    bySat[sat].days.push(d);
  }
  return out;
}

// Weekends the rhythm has off
export function freeWeekends(rhythm, start, end) {
  if (rhythm !== 'A' && rhythm !== 'B') return [];
  return weekendsInRange(start, end).filter((w) => w.side !== rhythm);
}

// Saturdays the rhythm has off (for the calendar preview)
export function freeSaturdaySet(rhythm, start, end) {
  return new Set(freeWeekends(rhythm, start, end).map((w) => w.saturday));
}

function dayMonth(d) {
  const x = parseDate(d);
  return { day: x.getUTCDate(), mon: MONTHS[x.getUTCMonth()] };
}

// [['2026-10-17','2026-10-18'], ['2026-10-31','2026-11-01'], ['2027-01-02']]
// → "17–18 Oct, 31 Oct–1 Nov, 2 Jan". Accepts weekend objects or day lists.
export function formatWeekends(list) {
  return list.map((w) => {
    const days = Array.isArray(w) ? w : w.days;
    const a = dayMonth(days[0]);
    if (days.length === 1) return `${a.day} ${a.mon}`;
    const b = dayMonth(days[days.length - 1]);
    return a.mon === b.mon ? `${a.day}–${b.day} ${b.mon}` : `${a.day} ${a.mon}–${b.day} ${b.mon}`;
  }).join(', ');
}

// "9 Oct 2026" from an ISO timestamp
export function formatAssignedAt(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}
